import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, cachedGet } from "@/lib/api";
import { formatMoney, toEUR, isUSBank, primaryCurrency } from "@/lib/format";
import { useFxRates } from "@/hooks/useFxRates";
import { SWITCH_SAVE } from "@/constants/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

export const SwitchAndSave = () => {
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("nordics");
  const [banks, setBanks] = useState([]);
  const [monthlyFee, setMonthlyFee] = useState([8]);
  const [transfers, setTransfers] = useState([3]);
  const [avgFee, setAvgFee] = useState([6]);
  useFxRates();

  useEffect(() => {
    cachedGet("/regions").then((r) => setRegions(r.data.filter((x) => !x.coming_soon)));
  }, []);

  useEffect(() => {
    api.get("/banks", { params: { region, fields: "full", page_size: 1 } }).then((r) => setBanks(r.data.items));
  }, [region]);

  const cheapest = banks[0];
  const currency = primaryCurrency(cheapest);
  const payNowYearly = monthlyFee[0] * 12 + transfers[0] * 12 * avgFee[0];
  const cheapestYearly = cheapest
    ? (isUSBank(cheapest) ? cheapest.fees.monthly_account_fee.value : toEUR(cheapest.fees.monthly_account_fee.value, cheapest.currency)) * 12
      + transfers[0] * 12 * (isUSBank(cheapest) ? cheapest.fees.intl_wire_fee.value : toEUR(cheapest.fees.intl_wire_fee.value, cheapest.currency))
    : 0;
  const savings = Math.max(0, Math.round(payNowYearly - cheapestYearly));

  return (
    <section className="bg-[#0F1115] text-white py-24 px-6 lg:px-10">
      <div className="max-w-5xl mx-auto">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#059669] mb-3">Switch &amp; Save</p>
        <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl">How much is your bank really costing you?</h2>
        <p className="mt-4 text-white/60 max-w-2xl">
          Drag the sliders to match your habits. We'll estimate your yearly savings if you switched to the cheapest bank we track in your region — all shown in {currency} for easy comparison.
        </p>

        <div className="mt-6" data-testid={SWITCH_SAVE.regionSelect}>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-60 rounded-none border-white/20 bg-transparent text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-stretch">
          <div className="space-y-10">
            <div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-white/70">Monthly account fee you pay now</span>
                <span className="font-display font-bold">{formatMoney(monthlyFee[0], currency)} / mo</span>
              </div>
              <Slider data-testid={SWITCH_SAVE.monthlyFeeSlider} min={0} max={50} step={1} value={monthlyFee} onValueChange={setMonthlyFee} />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-white/70">International transfers per month</span>
                <span className="font-display font-bold">{transfers[0]} / mo</span>
              </div>
              <Slider data-testid={SWITCH_SAVE.transfersSlider} min={0} max={10} step={1} value={transfers} onValueChange={setTransfers} />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-4">
                <span className="text-white/70">Average fee per transfer now</span>
                <span className="font-display font-bold">{formatMoney(avgFee[0], currency)}</span>
              </div>
              <Slider data-testid={SWITCH_SAVE.avgFeeSlider} min={0} max={50} step={1} value={avgFee} onValueChange={setAvgFee} />
            </div>
          </div>

          <div className="border border-white/10 p-8 flex flex-col justify-center items-center text-center">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/40 mb-2">You could save</p>
            <p data-testid={SWITCH_SAVE.yearlySavings} className="font-display font-black text-5xl text-[#059669]">
              {formatMoney(savings, currency)}<span className="text-lg text-white/50"> / year</span>
            </p>
            <div className="mt-8 w-full space-y-2 text-sm">
              <div className="flex justify-between border-t border-white/10 pt-3">
                <span className="text-white/60">You pay now</span>
                <span className="font-semibold">{formatMoney(Math.round(payNowYearly), currency)} / yr</span>
              </div>
              <div className="flex justify-between border-t border-white/10 pt-3">
                <span className="text-white/60">Cheapest: {cheapest?.name || "—"}</span>
                <span className="font-semibold text-[#059669]">{formatMoney(Math.round(cheapestYearly), currency)} / yr</span>
              </div>
            </div>
            <p className="mt-6 text-[11px] text-white/30">
              Estimate only — based on audited fees for the selected region{currency === "EUR" ? ", converted to EUR at live reference rates" : ", shown natively in USD"}.
            </p>
            <Link
              to="/rankings"
              className="mt-6 inline-flex items-center gap-2 bg-[#059669] text-white px-6 py-3 text-sm font-semibold hover:bg-white hover:text-[#0F1115] transition-colors duration-200"
            >
              See the cheapest banks
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
